import React, { useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  CircularProgress,
  Grid,
} from '@mui/material';
import { formatNZTime } from '../../../utils/dateUtils.ts';

interface AuditLogDetailDialogProps {
  id: number | string;
  open: boolean;
  onClose: () => void;
}

const prettyJson = (value?: string | null) => {
  if (!value) return null;
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>{label}</Typography>
      <Typography variant="body2" sx={{ color: '#1e293b', wordBreak: 'break-word' }}>{value}</Typography>
    </Grid>
  );
};

const JsonBlock: React.FC<{ label: string; json?: string | null }> = ({ label, json }) => {
  const formatted = prettyJson(json);
  if (!formatted) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ color: '#334155', mb: 0.5, fontWeight: 700 }}>{label}</Typography>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          bgcolor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          fontSize: '0.78rem',
          overflowX: 'auto',
          maxHeight: 260,
        }}
      >
        {formatted}
      </Box>
    </Box>
  );
};

const AuditLogDetailDialog: React.FC<AuditLogDetailDialogProps> = ({ id, open, onClose }) => {
  const dataProvider = useDataProvider();
  const [record, setRecord] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    dataProvider.getOne('auditLog', { id })
      .then(({ data }: any) => setRecord(data))
      .finally(() => setLoading(false));
  }, [open, id, dataProvider]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ fontWeight: 800 }}>
        Audit Log Detail
        {record && (
          <Chip
            size="small"
            label={record.operation}
            sx={{ ml: 1.5, fontWeight: 700, bgcolor: '#eef2ff', color: '#3730a3' }}
          />
        )}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5 }}>
        {loading || !record ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Field label="Log Time" value={record.logTime ? formatNZTime(record.logTime) : undefined} />
              <Field label="Operation" value={record.operation} />
              <Field label="Category" value={record.category} />
              <Field label="Module" value={record.module} />
              <Field label="Entity" value={record.entityName ? `${record.entityName} #${record.entityId ?? '-'}` : undefined} />
              <Field label="Result" value={record.result} />
              <Field label="Severity" value={record.severity} />
              <Field label="User" value={record.userName ? `${record.userName} (#${record.userId ?? '-'})` : undefined} />
              <Field label="Role" value={record.roleName} />
              <Field label="Department" value={record.department} />
              <Field label="IP Address" value={record.ipAddress} />
              <Field label="Browser" value={record.browser ? `${record.browser} ${record.browserVersion ?? ''}`.trim() : undefined} />
              <Field label="OS / Device" value={[record.operatingSystem, record.device].filter(Boolean).join(' / ') || undefined} />
              <Field label="Request" value={record.requestMethod ? `${record.requestMethod} ${record.requestUrl ?? ''}` : undefined} />
              <Field label="Response Code" value={record.responseCode} />
              <Field label="Duration" value={record.durationMs != null ? `${record.durationMs} ms` : undefined} />
              <Field label="Trace Id" value={record.traceId} />
              <Field label="Description" value={record.description} />
            </Grid>

            {record.errorMessage && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px' }}>
                <Typography variant="subtitle2" sx={{ color: '#991b1b', fontWeight: 700 }}>Error</Typography>
                <Typography variant="body2" sx={{ color: '#991b1b' }}>{record.errorMessage}</Typography>
              </Box>
            )}

            <JsonBlock label="Changed Fields" json={record.changedFields} />
            <JsonBlock label="Old Data" json={record.oldData} />
            <JsonBlock label="New Data" json={record.newData} />
            <JsonBlock label="Request Body" json={record.requestBody} />
            <JsonBlock label="Search Condition" json={record.searchCondition} />
            <JsonBlock label="Stack Trace" json={record.stackTrace} />
          </>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuditLogDetailDialog;
